const matchesColumn = (column, matchKey) => {
  if (!matchKey) return false;

  if (column.key === matchKey || column.dataIndex === matchKey) {
    return true;
  }

  if (Array.isArray(column.dataIndex)) {
    return column.dataIndex.join(".") === matchKey;
  }

  return false;
};

const getFieldValue = (record, field) => record?.[field];

export const tableSorters = {
  number:
    (field) =>
    (a, b) =>
      (getFieldValue(a, field) ?? 0) - (getFieldValue(b, field) ?? 0),

  string:
    (field) =>
    (a, b) =>
      String(getFieldValue(a, field) ?? "").localeCompare(
        String(getFieldValue(b, field) ?? ""),
      ),

  date:
    (field) =>
    (a, b) =>
      new Date(getFieldValue(a, field) ?? 0).getTime() -
      new Date(getFieldValue(b, field) ?? 0).getTime(),

  custom: (compareFn) => compareFn,
};

/**
 * Normalizes defaultSort into a compare function + order.
 * Accepts:
 * - (a, b) => number
 * - { key, order?, type? }
 * - { compare: (a, b) => number, order? }
 */
export function resolveDefaultSort(defaultSort) {
  if (!defaultSort) return null;

  if (typeof defaultSort === "function") {
    return { compare: defaultSort, order: "ascend" };
  }

  const {
    key,
    field,
    columnKey,
    compare,
    order = "ascend",
    type,
  } = defaultSort;

  if (typeof compare === "function") {
    return {
      compare,
      order,
      matchKey: columnKey ?? field ?? key,
    };
  }

  const sortKey = key ?? field ?? columnKey;
  if (!sortKey) return null;

  const compareFn =
    type === "string"
      ? tableSorters.string(sortKey)
      : type === "date"
        ? tableSorters.date(sortKey)
        : tableSorters.number(sortKey);

  return {
    compare: compareFn,
    order,
    matchKey: sortKey,
  };
}

/** Wraps a compare function so it respects sort order (negates on "descend"). */
function makeOrderedCompare(resolved) {
  if (!resolved?.compare) return null;
  const { compare, order } = resolved;
  return order === "descend" ? (a, b) => -compare(a, b) : compare;
}

/** Extracts the compare function from a column's `sorter` (function or { compare }). */
function getColumnCompare(column) {
  const sorter = column?.sorter;
  if (typeof sorter === "function") return sorter;
  if (sorter && typeof sorter.compare === "function") return sorter.compare;
  return null;
}

/**
 * Normalizes defaultSortToColumn into { columnKey, order }.
 * Accepts:
 * - "column_key" (string)
 * - { columnKey | key | field, order? }
 */
export function resolveColumnSort(defaultSortToColumn) {
  if (!defaultSortToColumn) return null;

  if (typeof defaultSortToColumn === "string") {
    return { columnKey: defaultSortToColumn, order: "ascend" };
  }

  const { columnKey, key, field, order = "ascend" } = defaultSortToColumn;
  const matchKey = columnKey ?? key ?? field;
  if (!matchKey) return null;

  return { columnKey: matchKey, order };
}

/** Whether defaultSort defines a usable primary ("first order") compare. */
export function hasPrimarySort(defaultSort) {
  return !!makeOrderedCompare(resolveDefaultSort(defaultSort));
}

/** Initial controlled sort state derived from defaultSortToColumn. */
export function initialSortState(defaultSortToColumn) {
  const columnSort = resolveColumnSort(defaultSortToColumn);
  return columnSort
    ? { columnKey: columnSort.columnKey, order: columnSort.order }
    : null;
}

/**
 * Builds controlled-sort columns + a data sorter that keeps `defaultSort` (the
 * primary "first order", e.g. is_paused) fixed, and sorts within that group by the
 * active column (sortState). Sortable columns are handed to antd as `sorter: true`
 * so antd shows the sort UI but does NOT locally re-sort (which would otherwise
 * negate the whole comparator and flip the is_paused grouping on "descend").
 */
export function getControlledSort({ columns = [], defaultSort, sortState }) {
  const primaryCompare = makeOrderedCompare(resolveDefaultSort(defaultSort));

  let secondaryCompare = null;
  if (sortState?.columnKey && sortState?.order) {
    const activeColumn = columns.find((c) =>
      matchesColumn(c, sortState.columnKey),
    );
    const compare = getColumnCompare(activeColumn);
    if (compare) {
      secondaryCompare =
        sortState.order === "descend" ? (a, b) => -compare(a, b) : compare;
    }
  }

  const sortData = (data) => {
    if (!Array.isArray(data) || data.length < 2) return data;
    if (!primaryCompare && !secondaryCompare) return data;

    return [...data].sort((a, b) => {
      if (primaryCompare) {
        const result = primaryCompare(a, b);
        if (result !== 0) return result;
      }
      return secondaryCompare ? secondaryCompare(a, b) : 0;
    });
  };

  const tableColumns = columns.map((column) => {
    if (!column.sorter) return column;
    const isActive = matchesColumn(column, sortState?.columnKey);
    return {
      ...column,
      sorter: true,
      sortOrder: isActive ? sortState.order : null,
    };
  });

  return { tableColumns, sortData };
}

/**
 * Legacy path (no defaultSort): just marks the defaultSortToColumn column with a
 * defaultSortOrder so antd's own local sorting kicks in on first render.
 */
export function applyDefaultSortToColumns(columns, defaultSortToColumn) {
  const columnSort = resolveColumnSort(defaultSortToColumn);
  if (!columnSort || !columns?.length) return columns;

  return columns.map((column) => {
    if (!matchesColumn(column, columnSort.columnKey) || !column.sorter) {
      return column;
    }
    return { ...column, defaultSortOrder: columnSort.order };
  });
}
